import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import UploaderMultiple from 'components/UploaderMultiple';
import HonorariosEntidades from 'components/MatriculateRequest/HonorariosEntidades';
import {
  getAllConsultoriosAsDirector,
  getAllConsultoriosCokiba,
  getConsultorioUpdateDirectorRequest,
  putConsultorioUpdateDirector,
} from '../../features/consultorios/consultoriosAPI';
import { getMatriculateInfo } from '../../features/matriculates/matriculatesAPI';
import {
  ConsultorioCokibaBD,
  ConsultorioUpdateDirector,
} from 'types/Consultorio';

interface MatriculateInfo {
  matricula: number;
  nombre: string;
}

export default function EditConsultoriosUpdateDirector() {
  const { id } = useParams<any>();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof ConsultorioUpdateDirector, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorios, setConsultorios] = useState<ConsultorioCokibaBD[]>([]);
  const [search, setSearch] = useState('');

  const [matriculateDirector, setMatriculateDirector] = useState<MatriculateInfo>({
    matricula: 0,
    nombre: '',
  });

  const [esStaff, setEsStaff] = useState(false);

  const [canUpdateByState, setCanUpdateByState] = useState(false);
  const [canUpdateAttachs, setCanUpdateAttachs] = useState(false);

  const [data, setData] = useState<ConsultorioUpdateDirector>({
    id: 0,
    state_id: null,
    matriculate_id: null,
    consultorio_id: null,
    matriculate_new: null,
    created_at: null,
    resolved_at: null,
    deleted_at: null,
    motivo_rechazo: null,
    user_id: null,
    state: null,
    attachs: [],
  });

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        getAllConsultoriosAsDirector()
          .then(response => {
            setConsultorios(response);
          })
          .catch(err => {
            alert(true, err.getMessage() || 'No se puedieron obtener los pedidos del matriculado');
          })
          .finally(() => {
            setLoading(false);
          });

        const updateRequest: ConsultorioUpdateDirector =
          await getConsultorioUpdateDirectorRequest(id) as ConsultorioUpdateDirector;
        if (!!updateRequest) {
          setData({
            ...updateRequest,
          });
        } else {
          throw new Error('No se pudo recuperar la solicitud de consultorio');
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultorioUpdateDirectorCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!!data.id) {
      const director = `${data.matriculate_new}`;
      setSearch(director);
      onSearch(director);
    }
  }, [data.id]);

  useEffect(() => {
    if (data.state_id == 3) {
      setCanUpdateByState(true);
      setCanUpdateAttachs(true);
    }
  }, [data.state_id]);

  useEffect(() => {
    if (!!data.matriculate_new && !!data.consultorio_id) {
      setLoading(true);

      getAllConsultoriosCokiba(`${data.matriculate_new}`)
        .then(response => {
          const exists = response.some(consultorio => consultorio.idConsultorioHabilitado === data.consultorio_id);

          setEsStaff(exists);

          const field = document.getElementById('search');
          field?.classList.remove('is-invalid');
        })
        .catch(err => {
          alert(true, err.getMessage() || 'No se puedieron obtener los consultorios del matriculado');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    data.matriculate_new,
    data.consultorio_id,
  ]);

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const consultorioUpdate: Partial<ConsultorioUpdateDirector> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorioUpdate[key as keyof ConsultorioUpdateDirector] = value.toString() as any;
    });

    // Seteamos el id
    consultorioUpdate['id'] = data.id;

    if (!data.matriculate_new) {
      const field = document.getElementById('search');
      field?.classList.add('is-invalid');
      alert(
        'Es requerido hacer la búsqueda del matriculado.',
      );
      setLoading(false);
      return;
    }
    consultorioUpdate['matriculate_new'] = data.matriculate_new;

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    // Enviamos la petición
    await putConsultorioUpdateDirector(consultorioUpdate, files)
      .then(res => {
        if (!res || res.status != 'ok') {
          if (!!res.message) {
            alert(res.message);
          } else {
            // Error interno
            console.error('ConsultoriosUpdateDirectorCreate', 'Error al enviar la solicitud');
            console.info(res);
            alert(
              'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
            );
          }
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosUpdateDirectorEdit',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | boolean | null;
    value = target.value;

    if (target.type === 'checkbox') {
      value = target.checked;
    }

    if (
      typeof value === 'string' &&
      ['consultorio_id'].includes(name)
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  useEffect(() => {
    setData({
      ...data,
      matriculate_new: null,
    });
  }, [search]);

  const onSearch = async (query: string) => {
    try {
      if (query) {
        setLoading(true);

        setMatriculateDirector({
          matricula: 0,
          nombre: '',
        });
        setData({
          ...data,
          matriculate_new: null,
        });

        const matriculate: MatriculateInfo = await getMatriculateInfo(+query) as MatriculateInfo;
        setMatriculateDirector(matriculate);
        setData({
          ...data,
          matriculate_new: matriculate.matricula,
        });
      }
    } catch (err) {
      const error = err as any;
      console.error(error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content">
      { data.motivo_rechazo
        ?
          <Alert color="warning">
            <Row className="mb-2">
              <Col sm={12}>
                <span className='group-name observations'>
                  Motivo de rechazo
                  <p className="mt-2">
                    { data.motivo_rechazo }
                  </p>
                </span>
              </Col>
            </Row>
          </Alert>
        : null
      }
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Editar Actualización de Director del Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="search">
                        Buscar Matriculado Nuevo Director <Required />
                      </Label>
                      <InputGroup>
                        <Input
                          id="search"
                          name="search"
                          type="text"
                          placeholder="Buscar por Matrícula..."
                          onChange={ event => setSearch(event.target.value) }
                          onKeyUp={ event => event.key === 'Enter' && onSearch(search) }
                          disabled={isLoading || !canUpdateByState}
                          value={ search }
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          style={{
                            border: `1px solid '#dddddd'`,
                            borderBottomRightRadius: '.25rem',
                            borderTopRightRadius: '.25rem',
                            width: '40px',
                          }}
                          disabled={isLoading || !canUpdateByState}
                          onClick={ () => onSearch(search) }
                        >
                          <i className="fas fa-search"></i>
                        </button>
                      </InputGroup>
                    </Col>
                    <Col md="6">
                      <Label for="consultorio_id">
                        Consultorio <Required />
                      </Label>
                      <Input
                        id="consultorio_id"
                        name="consultorio_id"
                        type="select"
                        className="custom-select form-control"
                        value={data.consultorio_id ?? ''}
                        invalid={formValidation.consultorio_id === false}
                        onChange={handleInputChange}
                        disabled={isLoading || !canUpdateByState}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {consultorios?.map(item => {
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.nombre}, {item.calle} {item.numero}, {item.localidad?.MTR008_D_LOCALIDAD}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      { data.matriculate_new
                        ?
                          <div className="border rounded p-2 bg-light">
                            <div className="ml-2 mb-2">
                              <strong>Matrícula:</strong> {matriculateDirector.matricula}
                            </div>
                            <div className="ml-2 mb-2">
                              <strong>Nombre:</strong> {matriculateDirector.nombre}
                            </div>
                          </div>
                        : null
                      }
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Adjuntos</CardTitle>
                </Col>
                <Col sm={12}>
                  <HonorariosEntidades
                    titulosPagos={[
                      'TASA_MINISTERIO_CAMBIO_CONSULTORIO',
                      'ARBA_TIMBRADO_EN_BLANCO',
                      'ARBA_TASA_CAMBIO_DIRECTOR_TECNICO',
                    ]}
                  />
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="constancia_pagos">
                        Constancia de haber realizado los 3 pagos detallados anteriormente <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="constancia_pagos"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="solicitud_habilitacion">
                        Nota Tipo 4 <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="solicitud_habilitacion"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={['application/pdf']}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                { !esStaff
                  ?
                    <FormGroup>
                      <Row className="mt-4 mb-2">
                        <Col md="6">
                          <Label for="titulos_grado">
                            Títulos de grado <Required />
                          </Label><br/>
                          <UploaderMultiple
                            name="titulos_grado"
                            attachs={data.attachs}
                            required
                            canUpdateAttachs={canUpdateAttachs}
                            multiple
                          />
                        </Col>
                        <Col md="6">
                          <Label for="constancia_cuit">
                            Constancia de CUIT del profesional de la kinesiología que{' '}
                              se menciona en la nota de solicitud <Required />
                          </Label><br/>
                          <UploaderMultiple
                            name="constancia_cuit"
                            attachs={data.attachs}
                            required
                            canUpdateAttachs={canUpdateAttachs}
                            accept={['application/pdf']}
                            multiple
                          />
                        </Col>
                      </Row>
                    </FormGroup>
                  : null
                }
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="resolucion_ministerio">
                        Resolución Ministerial vigente <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="resolucion_ministerio"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={['application/pdf']}
                      />
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                { [3].includes(data.state_id || 0)
                  ?
                    <FormGroup>
                      <div className="text-center">
                        <Button
                          type="button"
                          className="btn-round"
                          onClick={handleSendForm}
                          disabled={isLoading || disableSend}
                        >
                          Enviar
                        </Button>
                      </div>
                    </FormGroup>
                  : null
                }
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
